import { useController, useFormContext } from 'react-hook-form';

import { getFormError } from 'constants/formErrors';
import { Option, OptionValue, TotemSelect } from 'components/TotemSelect';
import { TotemLabel } from '../TotemLabel';

type DetailFormSelectBaseProps<T extends OptionValue> = {
    label?: string;
    sublabel?: string;
    name: string;
    width?: string;
    required?: boolean;
    isClearable?: boolean;
    isDisabled?: boolean;
    dataTest?: string;
    placeholder?: string;
};

type SingleSelectProps<T extends OptionValue> = DetailFormSelectBaseProps<T> & {
    isMulti?: false;
    defaultValue?: T;
    options?: Option<T>[];
    fetchOptions?: (query: string) => Promise<Option<T>[]>;
    defaultOptions?: Option<T>[];
};

type MultiSelectProps<T extends OptionValue> = DetailFormSelectBaseProps<T> & {
    isMulti: true;
    defaultValue?: T[];
    options?: Option<T>[];
    fetchOptions?: (query: string) => Promise<Option<T>[]>;
    defaultOptions?: Option<T>[];
};

export type DetailFormSelectProps<T extends OptionValue> = SingleSelectProps<T> | MultiSelectProps<T>;

export const DetailFormSelect = <T extends OptionValue>({
    label,
    sublabel,
    name,
    defaultValue,
    width,
    dataTest = '',
    required = false,
    isClearable = false,
    isDisabled = false,
    isMulti = false,
    placeholder,
    options,
    fetchOptions,
    defaultOptions,
}: DetailFormSelectProps<T>) => {
    const {
        control,
        formState: { errors },
    } = useFormContext();
    const {
        field: { value, onChange },
    } = useController({
        name,
        control,
        rules: { required },
        defaultValue,
    });

    // @ts-expect-error to fix
    const error = required && getFormError(errors?.[name]?.type);

    return (
        <div className="relative flex flex-col gap-[5px]" style={{ width: width || '100%' }}>
            {label || sublabel ? (
                <div className="flex flex-col">
                    {label ? <TotemLabel>{label}</TotemLabel> : null}
                    {sublabel ? <span className="text-muted-foreground text-[13px]">{sublabel}</span> : null}
                </div>
            ) : null}
            <div className="relative" data-test={dataTest}>
                {isMulti ? (
                    <TotemSelect<T>
                        value={Array.isArray(value) ? value : []}
                        onChange={onChange}
                        isMulti
                        options={options}
                        placeholder={placeholder}
                        disabled={isDisabled}
                        isClearable={isClearable}
                        fetchOptions={fetchOptions}
                        defaultOptions={defaultOptions}
                    />
                ) : (
                    <TotemSelect<T>
                        value={value ?? null}
                        onChange={onChange}
                        options={options}
                        placeholder={placeholder}
                        disabled={isDisabled}
                        isClearable={isClearable}
                        fetchOptions={fetchOptions}
                        defaultOptions={defaultOptions}
                    />
                )}
                {error ? (
                    <div className="absolute right-[-2px] top-[-16px] w-max text-base text-destructive">{error}</div>
                ) : null}
            </div>
        </div>
    );
};
